import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  Clock,
  AmbientLight,
  DirectionalLight,
  AnimationMixer,
  AnimationClip,
  Mesh,
  Material,
  Group,
} from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

export interface ThreeContextParams {
  canvas?: HTMLCanvasElement;
  container?: HTMLElement;
  width: number;
  height: number;
  antialias?: boolean;
  backgroundColor?: number;
  backgroundAlpha?: number;
}

export default class ThreeContext {
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  clock: Clock;
  gltfLoader: GLTFLoader;
  mixers: Map<number, AnimationMixer> = new Map();
  models: Map<number, Group> = new Map();

  constructor(params: ThreeContextParams) {
    const {
      canvas,
      container,
      width,
      height,
      antialias = true,
      backgroundColor = 0x000000,
      backgroundAlpha = 1,
    } = params;

    this.scene = new Scene();

    this.camera = new PerspectiveCamera(75, width / height, 0.1, 1000);
    this.camera.position.z = 5;

    const rendererOpts: any = { antialias };
    if (canvas) {
      rendererOpts.canvas = canvas;
    }
    if (backgroundAlpha < 1) {
      rendererOpts.alpha = true;
    }

    this.renderer = new WebGLRenderer(rendererOpts);
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setClearColor(backgroundColor, backgroundAlpha);

    // If no canvas was provided but a container was, append the generated canvas
    if (!canvas && container) {
      container.appendChild(this.renderer.domElement);
    }

    const ambientLight = new AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);
    const directionalLight = new DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 10, 7.5);
    this.scene.add(directionalLight);

    this.clock = new Clock();
    this.gltfLoader = new GLTFLoader();
  }

  update() {
    const delta = this.clock.getDelta();
    for (const mixer of this.mixers.values()) {
      mixer.update(delta);
    }
    this.renderer.render(this.scene, this.camera);
  }

  async loadGLB(id: number, url: string): Promise<Group> {
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(url, (gltf) => {
        const model = gltf.scene;
        this.scene.add(model);
        this.models.set(id, model);

        if (gltf.animations && gltf.animations.length > 0) {
          const mixer = new AnimationMixer(model);
          this.mixers.set(id, mixer);
          model.userData.animations = gltf.animations;
        }

        resolve(model);
      }, undefined, (error) => {
        reject(error);
      });
    });
  }

  playAnimation(id: number, animationIndex: number, speed: number) {
    const mixer = this.mixers.get(id);
    const model = this.models.get(id);
    if (!mixer || !model) return;

    mixer.stopAllAction();
    const animations = model.userData.animations as AnimationClip[];
    if (animations && animations[animationIndex]) {
      const action = mixer.clipAction(animations[animationIndex]);
      action.timeScale = speed;
      action.play();
    }
  }

  stopAnimation(id: number) {
    const mixer = this.mixers.get(id);
    if (mixer) mixer.stopAllAction();
  }

  removeModel(id: number) {
    const model = this.models.get(id);
    if (model) {
      this.scene.remove(model);
      model.traverse((child) => {
        const mesh = child as Mesh;
        if (mesh.geometry) {
          mesh.geometry.dispose();
        }
        if (mesh.material) {
          const materials = Array.isArray(mesh.material)
            ? mesh.material as Material[]
            : [mesh.material as Material];
          materials.forEach(m => m.dispose());
        }
      });
      this.models.delete(id);
    }
    const mixer = this.mixers.get(id);
    if (mixer) {
      mixer.stopAllAction();
      this.mixers.delete(id);
    }
  }

  resize(width: number, height: number) {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  destroy() {
    for (const id of this.models.keys()) {
      this.removeModel(id);
    }
    this.renderer.dispose();
    this.renderer.domElement.remove();
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.clock = null;
    this.gltfLoader = null;
  }
}
